// LicenseSuspendedBanner — pinned banner above OpenItemsSummary surfacing
// active driver-license suspensions detected from MCCS records.
//
// Why this exists: license suspension is the #1 silent life problem for
// NJRC clients ("I can't drive to my job"). MCCS already tells us when
// the license has been administratively held — the Status field on
// time-payment / parking-collection records contains "DL Suspended" once
// a person has missed enough payments. We were already pulling that
// signal but doing nothing with it; this banner makes it actionable.
//
// What the banner does:
//   • Counts records with "DL Suspended" status (case-insensitive substring).
//   • If count > 0, renders a high-contrast crisis-red panel above the
//     existing OpenItemsSummary headline.
//   • Provides one-click links to the MVC restoration scrape result (if
//     present) AND a "Download pre-filled DO-21" button (existing endpoint
//     /api/jobs/:id/mvc-do21.pdf — fills name, DOB, address, license #).
//   • Hides itself entirely when no record carries the flag (most people).
//
// The same scan logic also runs server-side in /api/today (see
// today.ts:detectLicenseSuspensions) so the cross-person triage view sees
// the same set. We accept the duplicated logic — UI scan is fast and the
// server scan is authoritative; they SHOULD agree, but if they ever
// drift, the per-person banner is what staff trust day-to-day.

import type { RecordRow, RecordData } from "../types";

interface Props {
  jobId: string;
  records: RecordRow[];
}

function isRecordData(x: unknown): x is RecordData {
  return (
    !!x &&
    typeof x === "object" &&
    "summary" in (x as Record<string, unknown>) &&
    typeof (x as { summary: unknown }).summary === "object"
  );
}

function pickSafe(field: string | string[] | undefined): string {
  if (!field) return "";
  return Array.isArray(field) ? field.at(-1) ?? "" : field;
}

/** Find every record where Status (top-level summary OR any subPage)
 *  contains "DL Suspended". Returns the matching record IDs so we can
 *  link to specific cards if needed. */
function findSuspendedRecords(records: RecordRow[]): RecordRow[] {
  const out: RecordRow[] = [];
  for (const r of records) {
    if (!isRecordData(r.data)) continue;
    const statusBags: string[] = [];
    statusBags.push(pickSafe(r.data.summary.Status));
    for (const sp of r.data.subPages ?? []) {
      if (!sp.fields) continue;
      statusBags.push(pickSafe(sp.fields.Status));
    }
    const flagged = statusBags.some((s) => /\bDL\s+Suspended\b/i.test(s));
    if (flagged) out.push(r);
  }
  return out;
}

export function LicenseSuspendedBanner({ jobId, records }: Props): JSX.Element | null {
  const suspended = findSuspendedRecords(records);
  if (suspended.length === 0) return null;

  return (
    <section className="container station station-flush-top">
      <div
        className="card-elevated dark-tile overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #C13455 0%, #7A1F3A 100%)",
          color: "#fff",
          border: "none",
          boxShadow: "0 18px 36px -12px rgba(193, 52, 85, 0.45)",
        }}
      >
        <div className="px-5 py-4 sm:px-6 sm:py-5">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div className="flex items-baseline gap-3">
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  fontSize: 18,
                  fontWeight: 900,
                  background: "rgba(255,255,255,0.18)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  borderRadius: 4,
                  padding: "1px 8px",
                  letterSpacing: "0.04em",
                  fontFamily: "var(--font-mono)",
                }}
              >
                ⚠
              </span>
              <div
                className="eyebrow text-[11px]"
                style={{ color: "rgba(255,255,255,0.85)", letterSpacing: "0.2em" }}
              >
                Driver'
              </div>
            </div>
            <span
              className="text-[12px] tabular"
              style={{ color: "rgba(255,255,255,0.85)", fontFamily: "var(--font-mono)", letterSpacing: "0.06em" }}
            >
              {suspended.length} case{suspended.length === 1 ? "" : "s"} on file
            </span>
          </div>

          <h2
            className="display mt-2"
            style={{
              fontSize: "clamp(22px, 2.4vw, 32px)",
              lineHeight: 1.1,
              fontVariationSettings: '"opsz" 96, "SOFT" 30',
              color: "#fff",
            }}
          >
            License is currently being held by NJ MVC.
          </h2>

          <p
            className="mt-2"
            style={{
              color: "rgba(255,255,255,0.92)",
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontSize: "clamp(13px, 1.1vw, 15px)",
              lineHeight: 1.5,
              maxWidth: 720,
            }}
          >
            At least one MCCS case is in <code style={{ background: "rgba(255,255,255,0.15)", padding: "1px 5px", borderRadius: 3, fontStyle: "normal", fontSize: 13 }}>DL Suspended</code> status —
            driving privileges will not return until the underlying fines
            and the MVC restoration steps are both resolved. Start the
            restoration paperwork now while the rest of intake continues.
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <a
              href={`/api/jobs/${jobId}/mvc-do21.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md px-4 py-2 text-[13px] font-semibold transition hover:opacity-90"
              style={{
                background: "#fff",
                color: "#6b1313",
                fontFamily: "var(--font-mono)",
                letterSpacing: "0.06em",
                textDecoration: "none",
              }}
            >
              ⤓ Download pre-filled DO-21
            </a>
            <a
              href="#source-mvc-restoration"
              className="rounded-md px-3 py-2 text-[13px] font-semibold transition hover:bg-white/10"
              style={{
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.5)",
                fontFamily: "var(--font-mono)",
                letterSpacing: "0.06em",
                textDecoration: "none",
              }}
            >
              View restoration scrape →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
